class TimerAnnouncementBar extends HTMLElement {
  #intervalId = null;
  #endTimeStr = null;
  #useViewerTimezone = true;
  #autoRestart = false;
  #endBeforeDay = false;
  #target = null;
  #translations = null;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeTimer();
  }

  disconnectedCallback() {
    this.#clearTick();
  }

  initializeTimer() {
    this.#endTimeStr = (this.dataset.endTime || '').trim();
    this.#useViewerTimezone = String(this.dataset.timezone).toLowerCase() === 'true';
    this.#autoRestart = String(this.dataset.autoRestart).toLowerCase() === 'true';
    this.#endBeforeDay = String(this.dataset.endBeforeDay).toLowerCase() === 'true';
    this.#translations = JSON.parse(this.dataset.translations);

    this.$daysVal = this.querySelector('[data-timer-days] strong');
    this.$hoursVal = this.querySelector('[data-timer-hours] strong');
    this.$minsVal = this.querySelector('[data-timer-minute] strong');
    this.$secsVal = this.querySelector('[data-timer-second] strong');

    this.$daysLbl = this.querySelector('[data-timer-days] span');
    this.$hoursLbl = this.querySelector('[data-timer-hours] span');
    this.$minsLbl = this.querySelector('[data-timer-minute] span');
    this.$secsLbl = this.querySelector('[data-timer-second] span');

    if (!this.$daysVal || !this.$hoursVal || !this.$minsVal || !this.$secsVal) {
      console.warn('[TimerAnnouncementBar] Missing value nodes.');
      return;
    }

    this.#target = this.#resolveTargetFromDateString(
      this.#endTimeStr,
      this.#useViewerTimezone,
      this.#endBeforeDay
    );

    if (!this.#target) {
      console.warn('[TimerAnnouncementBar] Invalid or empty end_time. Freezing at 00:00:00:00.');
      this.#render(0);
      this.classList.add('is-finished');
      return;
    }

    const now = Date.now();
    if (this.#target.getTime() <= now) {
      if (this.#autoRestart) {
     
          this.#target = this.#nextBoundaryFrom(this.#target, this.#endBeforeDay);

        
          while (this.#target.getTime() <= Date.now()) {
            this.#target = this.#nextBoundaryFrom(this.#target, this.#endBeforeDay);
          }
      } else {
        this.#render(0);
        this.classList.add('is-finished');
        return;
      }
    }
    
    this.#render(this.#target.getTime() - Date.now());
    this.#startTick();
  }

  #startTick() {
    this.#clearTick();
    this.#intervalId = window.setInterval(() => {
      const remaining = this.#target.getTime() - Date.now();

      if (remaining <= 0) {
        this.#render(0);

        if (this.#autoRestart) {
          this.#target = new Date(Date.now() + 24 * 60 * 60 * 1000);
        } else {
          this.classList.add('is-finished');
          this.#clearTick();
        }
        return;
      }

      this.#render(remaining);
    }, 1000);
  }

  #clearTick() {
    if (this.#intervalId != null) {
      clearInterval(this.#intervalId);
      this.#intervalId = null;
    }
  }

  #resolveTargetFromDateString(endStr, useViewerTz, endBeforeDay) {
    if (!endStr) return null;

    const parts = endStr.split('/').map(p => p.trim());
    if (parts.length !== 3) return null;

    let [mm, dd, yyyy] = parts;
    const month = Number(mm);
    const day = Number(dd);
    const year = Number(yyyy);

    if (!Number.isInteger(month) || !Number.isInteger(day) || !Number.isInteger(year)) return null;
    if (year < 1000 || month < 1 || month > 12 || day < 1 || day > 31) return null;

    const h = endBeforeDay ? 0  : 23;
    const m = endBeforeDay ? 0  : 59;
    const s = endBeforeDay ? 0  : 59;
    const ms = endBeforeDay ? 0 : 999;

    if (useViewerTz) {
      return new Date(year, month - 1, day, h, m, s, ms);
    } else {
      const msUtc = Date.UTC(year, month - 1, day, h, m, s, ms);
      return new Date(msUtc);
    }
  }
  #nextBoundaryFrom(dateObj, endBeforeDay) {
    const y = dateObj.getFullYear();
    const m = dateObj.getMonth();
    const d = dateObj.getDate() + 1; 

    if (endBeforeDay) {
      return new Date(y, m, d, 0, 0, 0, 0);
    } else {
      return new Date(y, m, d, 23, 59, 59, 999);
    }
  }

  #render(msRemaining) {
    const ms = Math.max(0, msRemaining);

    const totalSeconds = Math.floor(ms / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    this.$daysVal.textContent = this.#pad2(days);
    this.$hoursVal.textContent = this.#pad2(hours);
    this.$minsVal.textContent = this.#pad2(minutes);
    this.$secsVal.textContent = this.#pad2(seconds);
    const {
      day_text,
      day_text_plural,
      hour_text,
      hour_text_plural,
      minutes_text,
      minutes_text_plural,
      seconds_text,
      seconds_text_plural
     } = this.#translations;
    this.#setLabel(this.$daysLbl, days, day_text, day_text_plural);
    this.#setLabel(this.$hoursLbl, hours, hour_text, hour_text_plural);
    this.#setLabel(this.$minsLbl, minutes,minutes_text, minutes_text_plural);
    this.#setLabel(this.$secsLbl, seconds, seconds_text, seconds_text_plural);
  }

  #pad2(n) {
    const s = String(n);
    return s.length >= 2 ? s : ('0' + s);
  }

  #setLabel(node, value, singular, plural) {
    if (!node) return;
    node.textContent = (value === 1) ? singular.toUpperCase() : plural.toUpperCase();
  }
}

customElements.define('timer-announcement-bar', TimerAnnouncementBar);